import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { ANNOUNCEMENTS_CHANGED_EVENT, announcementService } from '../services/announcementService';
import { t } from '../i18n';
import type { Announcement } from '../types/announcements';
import './NoticeBanner.css';

const DISMISS_KEY = 'dancemance.banner.dismissed';

function readDismissed(): string[] {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    return raw ? (JSON.parse(raw) as string[]) : [];
  } catch {
    return [];
  }
}

function writeDismissed(ids: string[]) {
  try {
    localStorage.setItem(DISMISS_KEY, JSON.stringify(ids.slice(-20)));
  } catch {
    /* ignore */
  }
}

export function NoticeBanner() {
  const { user } = useAuth();
  const [banner, setBanner] = useState<Announcement | null>(null);

  const load = useCallback(async () => {
    if (!user) {
      setBanner(null);
      return;
    }
    try {
      const rows = await announcementService.listMine();
      const dismissed = new Set(readDismissed());
      setBanner(rows.find((row) => row.active && !dismissed.has(row._id)) ?? null);
    } catch {
      setBanner(null);
    }
  }, [user]);

  useEffect(() => {
    load();
    window.addEventListener(ANNOUNCEMENTS_CHANGED_EVENT, load);
    window.addEventListener('focus', load);
    return () => {
      window.removeEventListener(ANNOUNCEMENTS_CHANGED_EVENT, load);
      window.removeEventListener('focus', load);
    };
  }, [load]);

  if (!banner) return null;

  return (
    <div className="notice-banner" role="status">
      <p>
        <strong>{t('banner.label')}</strong>
        <span>{banner.message}</span>
      </p>
      <button
        type="button"
        className="notice-banner__close"
        aria-label={t('banner.dismiss')}
        onClick={() => {
          writeDismissed([...readDismissed(), banner._id]);
          setBanner(null);
        }}
      >
        ×
      </button>
    </div>
  );
}
